import CancelIcon from "@mui/icons-material/Cancel"
import DeleteIcon from "@mui/icons-material/Delete"
import SaveIcon from "@mui/icons-material/Save"
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material"
import dayjs from "dayjs"
import { getAnalytics, logEvent } from "firebase/analytics"
import {
  collection,
  deleteDoc,
  doc,
  getFirestore,
  setDoc,
} from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import Color from "../Color"
import { timber_debug, timber_warn } from "../Timber"
import { Inventory } from "../Type"
import { formatCurrencyAmount } from "../utils/CurrencyUtils"

const InventoryDialog: FunctionComponent<{
  userId: string
  inventory: Inventory
  onClickClose: () => void
}> = ({ userId, inventory, onClickClose }) => {
  const intl = useIntl()

  // Firebase
  const firestore = getFirestore()
  const analytics = getAnalytics()
  const inventoriesRef = collection(firestore, `/users/${userId}/inventories`)

  const [inventoryId, setInventoryId] = useState<string>(inventory.id)
  const [inventoryName, setInventoryName] = useState<string>(inventory.name)
  const [inventoryUnitPrice, setInventoryUnitPrice] = useState<number | string>(
    formatCurrencyAmount(inventory.unitPrice)
  )
  const [inventoryQuantity, setInventoryQuantity] = useState<number | string>(
    inventory.quantity
  )
  const [inventoryInsertDate, setInventoryInsertDate] = useState<number>(
    inventory.insertDate
  )

  useEffect(() => {
    setInventoryId(inventory.id)
    setInventoryName(inventory.name)
    setInventoryUnitPrice(formatCurrencyAmount(inventory.unitPrice))
    setInventoryQuantity(inventory.quantity)
    setInventoryInsertDate(inventory.insertDate)
  }, [inventory])

  const saveInventory = () => {
    timber_debug(
      `Inventory id: ${inventoryId} name: ${inventoryName} unitPrice: ${inventoryUnitPrice} quantiy: ${inventoryQuantity}`
    )
    if (inventoryName === "") {
      timber_warn("Empty inventory name")
      return
    }
    const currentTime = dayjs().valueOf()
    if (inventoryId === "") {
      // Saves a new inventory
      const inventoryRef = doc(inventoriesRef)
      setDoc(inventoryRef, {
        name: inventoryName,
        unitPrice:
          inventoryUnitPrice !== "" ? (inventoryUnitPrice as number) * 100 : 0,
        quantity: inventoryQuantity !== "" ? inventoryQuantity : 0,
        insertDate: currentTime,
        lastEditDate: currentTime,
      })
        .then(() => {
          timber_debug("Inventory created successfully")
          onClickClose()
          logEvent(analytics, "web_inventory_created")
        })
        .catch((error) => {
          console.error(error)
          onClickClose()
        })
      logEvent(analytics, "web_create_inventory_clicked")
    } else {
      // Saves an edited inventory
      const inventoryRef = doc(firestore, inventoriesRef.path, inventoryId)
      setDoc(inventoryRef, {
        name: inventoryName,
        unitPrice:
          inventoryUnitPrice !== "" ? (inventoryUnitPrice as number) * 100 : 0,
        quantity: inventoryQuantity !== "" ? inventoryQuantity : 0,
        insertDate: inventoryInsertDate,
        lastEditDate: currentTime,
      })
        .then(() => {
          timber_debug("Inventory edited successfully")
          onClickClose()
          logEvent(analytics, "web_inventory_edited")
        })
        .catch((error) => {
          console.error(error)
          onClickClose()
        })
      logEvent(analytics, "web_edit_inventory_clicked")
    }
  }

  const deleteInventory = () => {
    logEvent(analytics, "web_delete_inventory_clicked")

    if (inventoryId !== "") {
      deleteDoc(doc(firestore, inventoriesRef.path, inventoryId))
        .then(() => {
          timber_debug(`Inventory with id ${inventoryId} deleted successfully`)
          onClickClose()
          logEvent(analytics, "web_inventory_deleted")
        })
        .catch((error) => {
          console.error(error)
          onClickClose()
        })
    } else {
      timber_warn("Unable to delete inventory: empty inventoryId")
    }
  }

  return (
    <div>
      <DialogTitle>
        {inventory.id === "" ? (
          <FormattedMessage id="dialog.title.create.inventory" />
        ) : (
          <FormattedMessage id="dialog.title.edit.inventory" />
        )}
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          direction={"row"}
          alignItems="center"
          rowSpacing={5}
          marginTop={1}
        >
          {/* Inventory name */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              required
              multiline
              label={intl.formatMessage({
                id: "table.headers.name",
              })}
              value={inventoryName}
              onChange={(event: any) => {
                if (event.target.value !== "") {
                  setInventoryName(event.target.value)
                } else {
                  setInventoryName("")
                }
              }}
            />
          </Grid>

          {/* Inventory unit price */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              type="number"
              label={intl.formatMessage({
                id: "table.headers.unit.price",
              })}
              onChange={(newValue: any) => {
                if (newValue.target.value !== "") {
                  setInventoryUnitPrice(parseFloat(newValue.target.value))
                } else {
                  setInventoryUnitPrice("")
                }
              }}
              value={inventoryUnitPrice}
            />
          </Grid>

          {/* Inventory quantity */}
          <Grid size={{ sm: 12, md: 12, lg: 12 }}>
            <TextField
              fullWidth
              type="number"
              label={intl.formatMessage({
                id: "table.headers.quantity",
              })}
              onChange={(newValue: any) => {
                if (newValue.target.value !== "") {
                  setInventoryQuantity(parseInt(newValue.target.value))
                } else {
                  setInventoryQuantity("")
                }
              }}
              value={inventoryQuantity}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClickClose}
          variant="outlined"
          startIcon={<CancelIcon />}
        >
          <FormattedMessage id="dialog.button.cancel" />
        </Button>
        {inventoryId !== "" && (
          <Button
            color="error"
            onClick={deleteInventory}
            variant="outlined"
            startIcon={<DeleteIcon />}
          >
            <FormattedMessage id="dialog.button.delete" />
          </Button>
        )}
        <Button
          onClick={saveInventory}
          variant="contained"
          style={{ background: Color.Primary }}
          startIcon={<SaveIcon />}
        >
          <FormattedMessage id="dialog.button.save" />
        </Button>
      </DialogActions>
    </div>
  )
}

export default InventoryDialog
